import { useState } from "react";
import { useSearchParams } from "react-router-dom";
import axios from "axios";
import { Check, Loader2, MailX } from "lucide-react";
import { useI18n } from "@/lib/i18n";
import { useSeo } from "@/hooks/useSeo";
import { useUnsubscribeNewsletter } from "@/hooks/useNewsletter";

function Desinscription() {
  const { t } = useI18n();
  const [searchParams] = useSearchParams();
  const [email, setEmail] = useState(searchParams.get("email") ?? "");
  const [error, setError] = useState("");
  const unsubscribe = useUnsubscribeNewsletter();

  useSeo({
    title: `${t("unsubscribe.heading")} — Ramba News`,
    description: t("unsubscribe.intro"),
    path: "/desinscription",
    noindex: true,
  });

  const handleSubmit = (e: React.FormEvent) => {
    e.preventDefault();
    if (!email.trim()) return;
    setError("");
    unsubscribe.mutate(email.trim(), {
      onError: (err) => {
        if (axios.isAxiosError(err) && err.response?.status === 404) {
          setError(t("unsubscribe.notFound"));
        } else {
          setError(t("unsubscribe.error"));
        }
      },
    });
  };

  return (
    <div className="mx-auto max-w-md px-4 sm:px-6 py-16">
      <h1 className="font-serif text-3xl font-semibold tracking-tight">{t("unsubscribe.heading")}</h1>

      {unsubscribe.isSuccess ? (
        <div className="mt-8 flex flex-col items-center gap-3 rounded-md border border-verified/30 bg-verified/5 px-6 py-10 text-center">
          <Check className="h-8 w-8 text-verified" />
          <p className="text-sm text-foreground">{t("unsubscribe.success")}</p>
        </div>
      ) : (
        <form onSubmit={handleSubmit} className="mt-6 space-y-4">
          <p className="text-sm text-muted-foreground">{t("unsubscribe.intro")}</p>
          <div className="space-y-1.5">
            <label className="text-xs font-semibold uppercase tracking-wider text-muted-foreground">
              {t("unsubscribe.emailLabel")}
            </label>
            <input
              type="email"
              required
              value={email}
              onChange={(e) => setEmail(e.target.value)}
              placeholder={t("unsubscribe.emailPlaceholder")}
              className="w-full rounded-md border border-input bg-background px-3 py-2 text-sm text-foreground placeholder-muted-foreground focus:outline-none focus:ring-1 focus:ring-foreground"
            />
          </div>
          {error && <p className="text-xs text-alert">{error}</p>}
          <button
            type="submit"
            disabled={unsubscribe.isPending}
            className="flex w-full items-center justify-center gap-2 rounded-md bg-foreground px-4 py-2.5 text-sm font-semibold text-background hover:bg-foreground/90 disabled:opacity-60 transition-colors"
          >
            {unsubscribe.isPending ? (
              <Loader2 className="h-4 w-4 animate-spin" />
            ) : (
              <MailX className="h-4 w-4" />
            )}
            {t("unsubscribe.confirm")}
          </button>
        </form>
      )}
    </div>
  );
}

export default Desinscription;
